/* The signed-in user's profile: one row, made by the database when the account is, read and
   updated here. Row-level security shows a user their own row and no other: the user id the
   functions take names the row, it does not grant it. */

import { z } from "@preflop/core";
import type { SupabaseClient } from "@supabase/supabase-js";

export const DISPLAY_NAME_MAX = 40;
/** The table allows 16 KB of settings; a little under, since jsonb is not its JSON text. */
export const SETTINGS_MAX_CHARS = 15000;

export const profileRowSchema = z.object({
  user_id: z.uuid(),
  display_name: z.string(),
  theme: z.string(),
  active_collection_id: z.uuid().nullable(),
  settings: z.record(z.string(), z.unknown()),
  created_at: z.string(),
});
export type ProfileRow = z.infer<typeof profileRowSchema>;

export interface Profile {
  userId: string;
  displayName: string;
  theme: string;
  activeCollectionId: string | null;
  settings: Record<string, unknown>;
  createdAt: string;
}

export const profileFromRow = (r: ProfileRow): Profile => ({
  userId: r.user_id,
  displayName: r.display_name,
  theme: r.theme,
  activeCollectionId: r.active_collection_id,
  settings: r.settings,
  createdAt: r.created_at,
});

/** What the client may change, checked before it is sent. The database checks again. */
export const profilePatchSchema = z
  .object({
    displayName: z.string().trim().max(DISPLAY_NAME_MAX),
    theme: z.string().regex(/^[a-z][a-z0-9-]{0,31}$/),
    activeCollectionId: z.uuid().nullable(),
    /** The whole object: it replaces what the profile held. */
    settings: z
      .record(z.string(), z.unknown())
      .refine(
        (s) => JSON.stringify(s).length <= SETTINGS_MAX_CHARS,
        "Settings are too large to save.",
      ),
  })
  .partial();
export type ProfilePatch = z.infer<typeof profilePatchSchema>;

const PROFILE_COLUMNS = "user_id, display_name, theme, active_collection_id, settings, created_at";

export interface ProfilesApi {
  getProfile(userId: string): Promise<Profile>;
  updateProfile(userId: string, patch: ProfilePatch): Promise<Profile>;
  /** Whether the signed-in user is an administrator: one of the few who manage the base
      ranges. The database answers for the caller alone, and it is the database that enforces
      it; the app asks only to know what to offer. */
  isAdmin(): Promise<boolean>;
}

export function createProfilesApi(db: SupabaseClient): ProfilesApi {
  return {
    async getProfile(userId) {
      const { data, error } = await db
        .from("profiles")
        .select(PROFILE_COLUMNS)
        .eq("user_id", userId)
        .single();
      if (error) throw new Error(`Could not read your profile: ${error.message}`);
      return profileFromRow(profileRowSchema.parse(data));
    },

    async isAdmin() {
      const { data, error } = await db.rpc("is_admin");
      if (error)
        throw new Error(`Could not tell whether you are an administrator: ${error.message}`);
      return data === true;
    },

    async updateProfile(userId, patch) {
      const p = profilePatchSchema.parse(patch);
      const row = {
        ...(p.displayName !== undefined ? { display_name: p.displayName } : {}),
        ...(p.theme !== undefined ? { theme: p.theme } : {}),
        ...(p.activeCollectionId !== undefined
          ? { active_collection_id: p.activeCollectionId }
          : {}),
        ...(p.settings !== undefined ? { settings: p.settings } : {}),
      };
      const { data, error } = await db
        .from("profiles")
        .update(row)
        .eq("user_id", userId)
        .select(PROFILE_COLUMNS)
        .single();
      if (error) throw new Error(`Could not save your profile: ${error.message}`);
      return profileFromRow(profileRowSchema.parse(data));
    },
  };
}
